import { makeAutoObservable, action, computed, reaction, runInAction } from '../../common/stores/mobx-config.js';
import timelineEventsStore from './timelineEventsStore.js';
import timelineFiltersStore from './timelineFiltersStore.js';
import HorizontalTimeline from './horizontalTimeline.js';
import rootPersonStore from '../../common/stores/rootPersonStore.js';
import gedcomDataStore from '../../gedcom/stores/gedcomDataStore.js';
import overlayManager from '../../utils/OverlayManager.js';
import { storeEvents, EVENTS } from '../../common/stores/storeEvents.js';
import { logger } from '../../utils/debug/logger.js';
import { getDecompressedEvents, decompressEventWithMetadata, getFirstName, getSurname } from '../../gedcom/formatters/individualFormatters.js';
import familyTownsStore from '../familyMap/stores/familyTownsStore.js';
import { TIMELINE_CONFIG } from './timelineConfig.js';
import historicalEventsStore, { dateSortKey } from './historicalEventsStore.js';
// Leaders data now loaded from KV via historicalEventsStore
/**
 * Store responsible for managing the timeline visualization.
 * Automatically synchronizes with root person changes and manages the horizontal timeline display.
 *
 * Reactive flow:
 *   filter change → filtersStore.filteredEvents (computed) →
 *   filtersReaction (debounced 150ms) → updateTimelineForRoot()
 */

class TimelineStore {
    // State management
    status = 'idle'; // 'idle' | 'loading' | 'success' | 'error'
    errorMessage = null;
    horizontalTimelineInstance = null;
    needsUpdate = false;

    constructor() {
        makeAutoObservable(this, {
            initializeTimeline: action,
            setStatus: action,
            clearTimeline: action,
            updateTimelineForRoot: action,

            // Computed
            currentTimelineHTML: computed,
            isLoading: computed,
            hasError: computed,

            // Non-observables
            horizontalTimelineInstance: false,
            setupReactions: false,
        });

        // React to root person changes
        this.rootReactionDisposer = reaction(
            () => rootPersonStore.root,
            async (newRoot) => {
                logger.timeline.store.debug("🔄 Reaction root déclenchée", newRoot);

                const timelineTab = document.querySelector('#tab-nav a[href="#tab4"]');
                const isTimelineVisible = timelineTab && timelineTab.classList.contains('active');

                if (newRoot) {
                    if (gedcomDataStore.isDirectImport && !isTimelineVisible) {
                        logger.timeline.store.debug("📥 Import direct + timeline non visible: traitement différé");
                        this.needsUpdate = true;
                        return;
                    }

                    if (isTimelineVisible) {
                        logger.timeline.store.info("📊 Timeline visible, mise à jour immédiate");
                        await this.updateTimelineForRoot();
                    } else {
                        logger.timeline.store.debug("📊 Timeline non visible, génération différée");
                        this.needsUpdate = true;
                    }
                } else {
                    this.clearTimeline();
                }
            },
            { name: 'TimelineStore-RootChangeReaction' }
        );

        // React to filter changes — debounced to avoid rapid consecutive rebuilds
        this.setupReactions();

        // Process deferred update when timeline tab is shown
        this.tabShownDisposer = storeEvents.subscribe(EVENTS.UI.TABS.TIMELINE_SHOWN, () => {
            if (this.needsUpdate && rootPersonStore.root) {
                logger.timeline.store.debug('📊 Onglet timeline affiché: traitement des données en attente');
                this.needsUpdate = false;
                this.updateTimelineForRoot();
            }
        });

        // Rebuild timeline when fresh historical events arrive from the worker.
        // The MobX reaction catches array length changes (stale localStorage → fresh data),
        // and the event subscription acts as a belt-and-suspenders fallback.
        this.historicalEventsDisposer = reaction(
            () => historicalEventsStore.events.length,
            () => {
                if (historicalEventsStore.isReady && this.status === 'success' && timelineFiltersStore.filters.showHistoricalEvents) {
                    logger.timeline.store.info('Historical events updated (reaction), rebuilding timeline');
                    this.updateTimelineForRoot();
                }
            },
            { name: 'TimelineStore-HistoricalEventsReaction' }
        );

        // Explicit subscription: always rebuild when worker fetch completes
        this.historicalLoadDisposer = storeEvents.subscribe(EVENTS.HISTORICAL_EVENTS.LOAD_COMPLETE, ({ source }) => {
            if (source === 'worker' && this.status === 'success' && timelineFiltersStore.filters.showHistoricalEvents) {
                logger.timeline.store.info('Historical events fetched from worker, rebuilding timeline');
                this.updateTimelineForRoot();
            }
        });
    }

    /**
     * Setup reactions that depend on other stores.
     * Observes filtersStore.filteredEvents and debounces rebuilds.
     */
    setupReactions() {
        this.filtersReactionDisposer = reaction(
            () => {
                // Track both the filtered events array identity and the hasActiveFilters flag
                // so that toggling a filter on/off always triggers
                const events = timelineFiltersStore.filteredEvents;
                const active = timelineFiltersStore.hasActiveFilters;
                return { length: events.length, active };
            },
            () => {
                // Only rebuild if the timeline has already been built at least once
                if (this.status === 'success' || this.status === 'loading') {
                    logger.timeline.store.debug('🔄 Filters changed, scheduling timeline rebuild');
                    this.updateTimelineForRoot();
                }
            },
            {
                name: 'TimelineStore-FiltersChangeReaction',
                delay: TIMELINE_CONFIG.FILTER_DEBOUNCE_MS,
            }
        );
    }

    // Computed properties
    get isLoading() {
        return this.status === 'loading';
    }

    get hasError() {
        return this.status === 'error';
    }

    get currentTimelineHTML() {
        return this.generateTimelineEvents();
    }

    // Actions
    setStatus(newStatus, error = null) {
        logger.timeline.store.debug(`🔄 Changement de status: ${this.status} -> ${newStatus}`);
        this.status = newStatus;
        this.errorMessage = error;
    }

    generateTimelineEvents() {
        const showHistorical = timelineFiltersStore.filters.showHistoricalEvents;
        const hasFamily = timelineEventsStore.hasEvents;

        if (!hasFamily && !showHistorical) {
            logger.timeline.store.warn("⚠️ No events to display");
            return '';
        }

        // --- Family events ---
        let groupedEvents = {};
        if (hasFamily) {
            const eventsToUse = timelineFiltersStore.hasActiveFilters
                ? timelineFiltersStore.filteredEvents
                : timelineEventsStore.events;

            logger.timeline.store.debug("🔍 Events for display:", eventsToUse.length);
            timelineEventsStore.setFilteredEvents(eventsToUse);

            groupedEvents = timelineEventsStore.getGroupedEvents();
        }

        const eventTypes = [
            { type: 'birth',    title: 'Naissances', format: event => timelineEventsStore.formatEvent(event, 'birth') },
            { type: 'death',    title: 'Décès',      format: event => timelineEventsStore.formatEvent(event, 'death') },
            { type: 'marriage', title: 'Mariages',    format: event => timelineEventsStore.formatEvent(event, 'marriage') }
        ];

        // --- Merge period keys (family + historical) ---
        const familyKeys = Object.keys(groupedEvents);

        // Compute family year range to constrain historical events
        let minYear = Infinity, maxYear = -Infinity;
        for (const key of familyKeys) {
            const year = parseInt(key.split('/')[2], 10);
            if (year < minYear) minYear = year;
            if (year > maxYear) maxYear = year;
        }

        // Historical periods: only those within family range ± 5 years
        let historicalPeriods = [];
        if (showHistorical && familyKeys.length > 0) {
            const allHistoricalKeys = historicalEventsStore.periodKeys;
            historicalPeriods = allHistoricalKeys.filter(key => {
                const year = parseInt(key.split('/')[2], 10);
                return year >= minYear - 5 && year <= maxYear + 5;
            });
        }

        // Generate ALL period keys between minYear and maxYear (continuous timeline)
        // so that empty periods still appear as date markers on the bar.
        const yearsGroup = TIMELINE_CONFIG.YEARS_GROUP;
        const eventPeriodKeys = new Set([...familyKeys, ...historicalPeriods]);
        const allPeriodKeys = [];

        if (minYear !== Infinity) {
            const startPeriod = Math.floor(minYear / yearsGroup) * yearsGroup;
            const endPeriod = Math.floor(maxYear / yearsGroup) * yearsGroup;
            for (let y = startPeriod; y <= endPeriod; y += yearsGroup) {
                allPeriodKeys.push(`01/01/${y}`);
            }
        } else {
            // Only historical events, no family — use their keys directly
            for (const key of [...eventPeriodKeys].sort((a, b) =>
                parseInt(a.split('/')[2], 10) - parseInt(b.split('/')[2], 10)
            )) {
                allPeriodKeys.push(key);
            }
        }

        if (allPeriodKeys.length === 0) {
            logger.timeline.store.info("🚫 Aucun événement groupé à afficher");
            return '';
        }

        logger.timeline.store.debug("📊 Total periods (family + historical):", allPeriodKeys.length);

        // --- Build HTML ---
        const parts = ['<div class="events-content" id="timeline-events-content"><ol>'];
        const { historicalScope, historicalCategory } = timelineFiltersStore.filters;

        for (const period of allPeriodKeys) {
            const timelineData = JSON.stringify({ date: period });
            const safeId = period.replace(/[^a-zA-Z0-9-]/g, '-');
            const periodYear = parseInt(period.split('/')[2], 10);

            parts.push(`<li class="box" id="timeline-event-${safeId}" data-horizontal-timeline='${timelineData}'>`);

            // --- Sub-group events by individual year within this period ---
            const yearData = {};
            for (let y = periodYear; y < periodYear + yearsGroup; y++) {
                yearData[y] = { family: { birth: [], death: [], marriage: [] }, historical: [] };
            }

            // Distribute family events by year
            if (groupedEvents[period]) {
                for (const { type } of eventTypes) {
                    const events = groupedEvents[period][type] || [];
                    for (const event of events) {
                        const eventYear = event.parsedDate ? event.parsedDate.getFullYear() : periodYear;
                        if (yearData[eventYear]) {
                            yearData[eventYear].family[type].push(event);
                        }
                    }
                }
            }

            // Distribute historical events by year
            if (showHistorical) {
                const historicalEvents = historicalEventsStore.getEventsForPeriod(
                    period, historicalScope, historicalCategory
                );
                for (const evt of historicalEvents) {
                    if (yearData[evt.year]) {
                        yearData[evt.year].historical.push(evt);
                    }
                }
            }

            // Count events per year
            const yearCounts = {};
            let totalEvents = 0;
            for (const y of Object.keys(yearData)) {
                const d = yearData[y];
                const count = d.family.birth.length + d.family.death.length + d.family.marriage.length + d.historical.length;
                yearCounts[y] = count;
                totalEvents += count;
            }

            if (totalEvents === 0) {
                // Empty period placeholder
                parts.push(`<p class="text-muted fst-italic">Aucun événement entre ${periodYear} et ${periodYear + yearsGroup}.</p>`);
            } else {
                // Find first non-empty year (default selection)
                const firstNonEmpty = Object.keys(yearCounts).find(y => yearCounts[y] > 0);

                // Year pills navigation with chevron arrows
                parts.push('<div class="year-pills-wrapper justify-content-center mb-2">');
                parts.push('<button class="year-pill-chevron year-pill-prev" data-direction="prev" aria-label="Année précédente"><i class="bi bi-chevron-left"></i></button>');
                parts.push('<ul class="nav nav-pills year-pills">');
                for (let y = periodYear; y < periodYear + yearsGroup; y++) {
                    const count = yearCounts[y];
                    const isActive = String(y) === firstNonEmpty;
                    const isEmpty = count === 0;

                    if (isEmpty) {
                        parts.push(
                            `<li class="nav-item">` +
                            `<button class="nav-link year-pill-empty" disabled data-year="${y}">${y}</button>` +
                            `</li>`
                        );
                    } else {
                        parts.push(
                            `<li class="nav-item">` +
                            `<button class="nav-link${isActive ? ' active' : ''}" data-year="${y}" data-period="${safeId}">${y} <span class="badge bg-secondary">${count}</span></button>` +
                            `</li>`
                        );
                    }
                }
                parts.push('</ul>');
                parts.push('<button class="year-pill-chevron year-pill-next" data-direction="next" aria-label="Année suivante"><i class="bi bi-chevron-right"></i></button>');
                parts.push('</div>');

                // Year content panels
                for (let y = periodYear; y < periodYear + yearsGroup; y++) {
                    if (yearCounts[y] === 0) continue;

                    const isActive = String(y) === firstNonEmpty;
                    parts.push(`<div class="year-panel${isActive ? ' active' : ''}" data-year-panel="${y}" data-period-panel="${safeId}"${!isActive ? ' style="display:none"' : ''}>`);

                    // Family events — horizontal scrollable rows (same pattern as historical)
                    const yd = yearData[y];
                    const hasAnyFamily = yd.family.birth.length + yd.family.death.length + yd.family.marriage.length > 0;
                    if (hasAnyFamily) {
                        const FAMILY_TYPES = [
                            { type: 'birth',    title: 'Naissances', icon: 'bi-sunrise',  color: '#2e7d32' },
                            { type: 'death',    title: 'Décès',      icon: 'bi-sunset',   color: '#37474f' },
                            { type: 'marriage', title: 'Mariages',   icon: 'bi-heart',    color: '#ad1457' },
                        ];
                        parts.push('<div class="family-events-section">');
                        for (const ft of FAMILY_TYPES) {
                            const events = yd.family[ft.type];
                            if (events.length === 0) continue;

                            parts.push(
                                `<div class="family-row">`,
                                `<div class="timeline-row-label" style="background-color:${ft.color}"><i class="bi ${ft.icon}"></i><span>${ft.title}</span></div>`,
                                '<div class="family-row-cards">'
                            );

                            for (const event of events) {
                                const ancestorInfo = event.sosa ? timelineEventsStore._getAncestorBranchAndGeneration(event.sosa) : null;
                                const branchColor = ancestorInfo?.branch === 'paternal' ? 'darkblue' : 'deeppink';
                                const genBadge = ancestorInfo
                                    ? `<span class="family-card-gen" style="color:${branchColor}">+${ancestorInfo.generation} gén.</span>`
                                    : '';

                                const personLink = event.personId
                                    ? `<a href="#" class="person-link text-decoration-none fw-bold" data-person-id="${event.personId}" data-show-details="true">${event.name}</a>`
                                    : `<span class="fw-bold">${event.name}</span>`;

                                const details = ft.type === 'death' && event.age
                                    ? `<span class="family-card-detail">${event.age} ans</span>`
                                    : ft.type === 'marriage' && event.spouse
                                    ? `<span class="family-card-detail">avec ${event.spouse}</span>`
                                    : '';

                                const townHtml = event.town
                                    ? `<span class="family-card-town">${event.town}</span>`
                                    : '';

                                parts.push(
                                    `<div class="family-card" style="border-left: 3px solid ${ft.color}">`,
                                    `<div class="family-card-topline">`,
                                    `<span class="family-card-date">${event.date || ''}</span>`,
                                    townHtml,
                                    `</div>`,
                                    `<span class="family-card-name">${personLink}</span>`,
                                    details,
                                    genBadge,
                                    '</div>'
                                );
                            }

                            parts.push('</div></div>');
                        }
                        parts.push('</div>');
                    }

                    // Historical events
                    if (yd.historical.length > 0) {
                        parts.push(this._renderHistoricalSection(yd.historical, y));
                    }

                    parts.push('</div>');
                }
            }

            parts.push('</li>');
        }

        parts.push('</ol></div>');
        return parts.join('');
    }

    /**
     * Setup event delegation for year pill navigation within periods.
     * Handles: pill clicks, chevron prev/next, Shift+ArrowLeft/Right keyboard.
     */
    _setupYearPillHandlers(container) {
        // Direct pill click
        container.addEventListener('click', (e) => {
            const pill = e.target.closest('.nav-link[data-year]');
            if (pill && !pill.disabled) {
                this._selectYearPill(pill);
                return;
            }

            // Chevron click
            const chevron = e.target.closest('.year-pill-chevron');
            if (chevron) {
                const wrapper = chevron.closest('.year-pills-wrapper');
                const direction = chevron.dataset.direction;
                this._navigateYearPill(wrapper, direction);
            }
        });

        // Keyboard navigation state for categories/events
        this._navCatIdx = 0;
        this._navCardIdx = -1; // -1 = category label, 0+ = card
        this._navFocused = null;

        this._yearPillKeyHandler = (e) => {
            const tag = e.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

            // Only act when timeline tab is visible
            const timelineTab = document.querySelector('#tab-nav a[href="#tab4"]');
            if (!timelineTab || !timelineTab.classList.contains('active')) return;

            const visiblePeriod = container.querySelector('.events-content li.box.selected');
            if (!visiblePeriod) return;

            // Shift+←/→: year pills navigation
            if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                const wrapper = visiblePeriod.querySelector('.year-pills-wrapper');
                if (!wrapper) return;
                e.preventDefault();
                this._navigateYearPill(wrapper, e.key === 'ArrowLeft' ? 'prev' : 'next');
                return;
            }

            // ↑/↓: category navigation
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                const activePanel = visiblePeriod.querySelector('.year-panel[style=""], .year-panel.active');
                if (!activePanel) return;
                const rows = activePanel.querySelectorAll('.historical-row, .family-row');
                if (rows.length === 0) return;

                e.preventDefault();
                const dir = e.key === 'ArrowDown' ? 1 : -1;
                const next = this._navCatIdx + dir;
                if (next >= 0 && next < rows.length) {
                    this._navCatIdx = next;
                    this._navCardIdx = -1;
                    rows[next].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
                this._setTimelineNavFocus(rows);
                return;
            }

            // Ctrl+←/→: event card navigation within category
            if (e.ctrlKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                const activePanel = visiblePeriod.querySelector('.year-panel[style=""], .year-panel.active');
                if (!activePanel) return;
                const rows = activePanel.querySelectorAll('.historical-row, .family-row');
                if (rows.length === 0 || this._navCatIdx >= rows.length) return;

                const cards = rows[this._navCatIdx].querySelectorAll('.historical-card, .family-card');
                if (cards.length === 0) return;

                e.preventDefault();
                const dir = e.key === 'ArrowRight' ? 1 : -1;
                this._navCardIdx = Math.max(-1, Math.min(cards.length - 1, this._navCardIdx + dir));
                if (this._navCardIdx >= 0) {
                    cards[this._navCardIdx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                }
                this._setTimelineNavFocus(rows);
                return;
            }
        };
        document.addEventListener('keydown', this._yearPillKeyHandler);
    }

    /**
     * Select a specific year pill and show its panel.
     */
    _selectYearPill(pill) {
        const year = pill.dataset.year;
        const pillsContainer = pill.closest('.year-pills');
        pillsContainer.querySelectorAll('.nav-link').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');

        const periodLi = pill.closest('li.box');
        periodLi.querySelectorAll('.year-panel').forEach(panel => {
            if (panel.dataset.yearPanel === year) {
                panel.style.display = '';
                panel.classList.add('active');
            } else {
                panel.style.display = 'none';
                panel.classList.remove('active');
            }
        });

        // Recalculate events-content height after DOM update
        const eventsContent = periodLi.closest('.events-content');
        if (eventsContent) {
            // Force reflow then measure the new height
            void periodLi.offsetHeight;
            eventsContent.style.height = periodLi.offsetHeight + 'px';
        }

    }

    /**
     * Set focus indicator on category label or event card.
     */
    _setTimelineNavFocus(rows) {
        if (this._navFocused) {
            this._navFocused.classList.remove('nav-focus');
            this._navFocused = null;
        }

        let el = null;
        if (this._navCardIdx >= 0) {
            const cards = rows[this._navCatIdx]?.querySelectorAll('.historical-card, .family-card');
            el = cards?.[this._navCardIdx];
        } else {
            el = rows[this._navCatIdx]?.querySelector('.timeline-row-label, .cat-label');
        }

        if (el) {
            el.classList.add('nav-focus');
            this._navFocused = el;
        }
    }

    /**
     * Navigate to prev/next non-empty year pill (wraps around).
     */
    _navigateYearPill(wrapper, direction) {
        const pills = [...wrapper.querySelectorAll('.nav-link:not(.year-pill-empty)')];
        if (pills.length === 0) return;

        const activeIndex = pills.findIndex(p => p.classList.contains('active'));
        let nextIndex = direction === 'prev' ? activeIndex - 1 : activeIndex + 1;

        // Wrap around: last → first, first → last
        if (nextIndex < 0) nextIndex = pills.length - 1;
        if (nextIndex >= pills.length) nextIndex = 0;

        this._selectYearPill(pills[nextIndex]);
    }

    /**
     * Render the historical context section HTML for a list of events within a period.
     */
    _renderHistoricalSection(events, year) {
        const CATEGORIES = [
            { key: 'politics',      label: 'Politique intérieure', icon: 'bi-bank' },
            { key: 'geopolitics',   label: 'Relations internationales', icon: 'bi-globe2' },
            { key: 'territory',     label: 'Extension territoriale', icon: 'bi-map' },
            { key: 'colonisation',  label: 'Colonisation',  icon: 'bi-globe-americas' },
            { key: 'science',       label: 'Sciences',      icon: 'bi-lightbulb' },
            { key: 'culture',       label: 'Culture',       icon: 'bi-palette' },
            { key: 'economy',       label: 'Économie',      icon: 'bi-graph-up' },
            { key: 'health',        label: 'Santé',         icon: 'bi-heart-pulse' },
            { key: 'society',       label: 'Société',       icon: 'bi-people' },
            { key: 'technology',    label: 'Technologie',   icon: 'bi-cpu' },
        ];
        const SCOPE_LABELS = { france: 'France', europe: 'Europe', mondial: 'Mondial' };

        // Category colors for parent bars
        const CAT_COLORS = {
            politics: '#1a5276', geopolitics: '#c0392b', territory: '#6c3483',
            colonisation: '#8b4513', science: '#2980b9', culture: '#8e44ad',
            economy: '#27ae60', health: '#e67e22', society: '#16a085', technology: '#2dd4bf'
        };

        // Group events by category
        const byCategory = {};
        for (const evt of events) {
            (byCategory[evt.category] || (byCategory[evt.category] = [])).push(evt);
        }

        // Find parent bars per category:
        // 1. Parents referenced by child events in this year
        // 2. Events that ARE parents themselves (isParent + endDate) in this year
        const parentsByCategory = {};
        if (year) {
            const referencedParentIds = new Set();
            for (const evt of events) {
                if (evt.parentId) referencedParentIds.add(evt.parentId);
            }

            const idx = historicalEventsStore.eventIndex;
            const candidates = [];

            // Parents referenced by children
            for (const id of referencedParentIds) {
                const evt = idx.get(id);
                if (evt?.endDate) candidates.push(evt);
            }

            // Events in this year that are themselves parents with duration
            for (const evt of events) {
                if (evt.isParent && evt.endDate && !candidates.some(c => c.id === evt.id)) {
                    candidates.push(evt);
                }
            }

            if (candidates.length > 0) {
                // Keep only lowest-level parents (remove grandparents)
                const candidateIds = new Set(candidates.map(e => e.id));
                for (const evt of candidates) {
                    if (evt.parentId && candidateIds.has(evt.parentId)) {
                        candidateIds.delete(evt.parentId);
                    }
                }
                for (const evt of candidates) {
                    if (!candidateIds.has(evt.id)) continue;
                    (parentsByCategory[evt.category] || (parentsByCategory[evt.category] = [])).push(evt);
                }
            }
        }

        const parts = [
            '<div class="historical-events-section">',
            '<h5 class="historical-events-header"><i class="bi bi-clock-history"></i> Contexte historique</h5>',
            '<div class="historical-rows">'
        ];

        // Collect IDs of parents shown as bars (to exclude from cards)
        const barParentIds = new Set();
        for (const parents of Object.values(parentsByCategory)) {
            for (const p of parents) barParentIds.add(p.id);
        }

        for (const cat of CATEGORIES) {
            const catEvents = byCategory[cat.key];
            // Filter out events that are shown as parent bars
            const filteredEvents = catEvents ? catEvents.filter(e => !barParentIds.has(e.id)) : [];
            const parents = parentsByCategory[cat.key];
            if ((!filteredEvents || filteredEvents.length === 0) && (!parents || parents.length === 0)) continue;

            // Parent bar(s) with hover summary + color coding when multiple
            const PARENT_COLORS = ['#e74c3c', '#2980b9', '#27ae60', '#8e44ad', '#f39c12'];
            const parentBarHtml = parents && parents.length > 0
                ? parents.map((p, i) => {
                    const startYear = p.year;
                    const endParts = p.endDate.split('/');
                    const endYear = endParts[endParts.length - 1];
                    const summaryHtml = p.summary
                        ? `<span class="historical-parent-bar-summary">${p.summary.replace(/</g, '&lt;')}</span>`
                        : '';
                    const hasWiki = p.wikiTitle && p.wikiTitle.trim() && p.summary;
                    const tag = hasWiki ? 'a' : 'div';
                    const linkAttrs = hasWiki
                        ? ` href="https://fr.wikipedia.org/wiki/${encodeURIComponent(p.wikiTitle)}" target="_blank" rel="noopener"`
                        : '';
                    // Color matches the left border on child cards
                    const bgColor = PARENT_COLORS[i % PARENT_COLORS.length];
                    return `<${tag} class="historical-parent-bar" style="background-color:${bgColor}"${linkAttrs}>` +
                        `<span class="historical-parent-bar-name">${p.title.replace(/</g, '&lt;')}</span>` +
                        `<span class="historical-parent-bar-dates">${startYear}–${endYear}</span>` +
                        summaryHtml +
                        `</${tag}>`;
                }).join('')
                : '';

            const wrappedBars = parentBarHtml
                ? `<div class="historical-parent-bars">${parentBarHtml}</div>`
                : '';

            parts.push(
                `<div class="historical-row">`,
                `<div class="timeline-row-label cat-label-${cat.key}"><i class="bi ${cat.icon}"></i><span>${cat.label}</span></div>`,
                '<div class="historical-row-content">',
                wrappedBars,
                '<div class="historical-row-cards">'
            );

            // Assign a color index per parent for visual distinction
            // Applied even with a single parent to distinguish attached vs unattached events
            const parentColorMap = new Map();
            if (parents && parents.length > 0) {
                parents.forEach((p, i) => parentColorMap.set(p.id, i));
            }

            const sortedCatEvents = [...filteredEvents].sort((a, b) => dateSortKey(a) - dateSortKey(b));
            for (const evt of sortedCatEvents) {
                const scopeLabel = SCOPE_LABELS[evt.scope] || evt.scope;

                const imgHtml = evt.imageUrl
                    ? `<img class="historical-card-img" src="${evt.imageUrl}" loading="lazy" alt="">`
                    : `<div class="historical-card-img-placeholder"><i class="bi ${cat.icon}"></i></div>`;

                const hasWiki = evt.wikiTitle && evt.wikiTitle.trim() && evt.summary;
                const tag = hasWiki ? 'a' : 'div';
                const linkAttrs = hasWiki
                    ? ` href="https://fr.wikipedia.org/wiki/${encodeURIComponent(evt.wikiTitle)}" target="_blank" rel="noopener"`
                    : '';

                const summaryHtml = evt.summary
                    ? `<span class="historical-card-summary">${evt.summary.replace(/</g, '&lt;')}</span>`
                    : '';

                // Color-coded left border + title color matching parent bar
                const colorIdx = evt.parentId ? parentColorMap.get(evt.parentId) : undefined;
                const parentColor = colorIdx !== undefined
                    ? PARENT_COLORS[colorIdx % PARENT_COLORS.length]
                    : null;
                const borderStyle = parentColor
                    ? ` style="border-left: 3px solid ${parentColor}"`
                    : '';
                const titleStyle = parentColor
                    ? ` style="color: ${parentColor}"`
                    : '';

                parts.push(
                    `<${tag} class="historical-card"${borderStyle} ${linkAttrs}>`,
                    '<div class="historical-card-visual">',
                    imgHtml,
                    `<span class="badge bg-light text-dark historical-card-scope">${scopeLabel}</span>`,
                    '</div>',
                    '<div class="historical-card-body">',
                    `<span class="historical-card-date">${evt.date}</span>`,
                    `<span class="historical-card-title"${titleStyle}>${evt.title}</span>`,
                    summaryHtml,
                    '</div>',
                    `</${tag}>`
                );
            }

            parts.push('</div></div></div>'); // close cards + content + row
        }

        parts.push('</div></div>');
        return parts.join('');
    }

    /**
     * Update the timeline for the current root person.
     * Generates HTML, destroys old instance, and creates a new HorizontalTimeline.
     */
    async updateTimelineForRoot() {
        logger.timeline.store.info("Updating timeline for root person");
        try {
            runInAction(() => {
                this.setStatus('loading');
            });

            const currentRoot = rootPersonStore.root;
            if (!currentRoot) {
                logger.timeline.store.warn("No root person defined");
                return;
            }

            if (!timelineEventsStore.hasEvents) {
                logger.timeline.store.warn("No events available");
                this.setStatus('success');
                return;
            }

            const timelineElement = document.getElementById("ascendantTimeline");
            if (!timelineElement) {
                throw new Error('Timeline element (#ascendantTimeline) not found');
            }

            // Show loading overlay
            overlayManager.show("tab4", {
                message: "Mise à jour de la chronologie...",
                customClass: "timeline-overlay"
            });

            // Generate HTML
            const html = this.currentTimelineHTML;
            if (!html || html.trim() === '') {
                logger.timeline.store.warn("No HTML generated - no events to display");
                timelineElement.innerHTML = '<div class="alert alert-info m-3">Aucun événement à afficher.</div>';
                timelineElement.style.opacity = "1";
                this.horizontalTimelineInstance = null;
                overlayManager.hide("tab4");
                this.setStatus('success');
                return;
            }

            // Destroy old instance (removes all listeners and generated DOM)
            this.cleanupTimelineInstance();

            // Prepare element for new content
            timelineElement.style.opacity = "0";
            timelineElement.style.transition = "opacity 0.3s ease-in";
            timelineElement.innerHTML = html;

            // Setup year pill navigation handlers (clicks + keyboard)
            this._cleanupYearPillKeyHandler();
            this._setupYearPillHandlers(timelineElement);

            // Force reflow so the browser processes the new DOM
            void timelineElement.offsetHeight;

            // Initialize new timeline instance (synchronous)
            this.initializeHorizontalTimeline();

            // Fade in
            timelineElement.style.opacity = "1";
            overlayManager.hide("tab4");
            this.setStatus('success');

            logger.timeline.store.info(`Timeline updated with ${timelineEventsStore.filteredEvents?.length || 0} events`);

        } catch (error) {
            logger.timeline.store.error("Error in updateTimelineForRoot:", error);
            runInAction(() => {
                this.setStatus('error', error.message);
            });
            overlayManager.hide("tab4");
        }
    }

    /**
     * Initialisation de la timeline horizontale
     */
    initializeHorizontalTimeline() {
        const timelineElement = document.getElementById("ascendantTimeline");
        if (!timelineElement) {
            throw new Error("Timeline element (#ascendantTimeline) not found");
        }

        const timelineItems = timelineElement.querySelectorAll('li[data-horizontal-timeline]');
        if (timelineItems.length === 0) {
            logger.timeline.store.warn("No events to display");
            timelineElement.innerHTML = '<div class="alert alert-info m-3">Aucun événement à afficher avec les filtres actuels. Veuillez ajuster les filtres.</div>';
            timelineElement.style.opacity = "1";
            this.horizontalTimelineInstance = null;
            this.setStatus('success');
            return;
        }

        // Validate all items have valid date JSON
        for (const item of timelineItems) {
            const dateAttr = item.getAttribute('data-horizontal-timeline');
            try {
                const parsed = JSON.parse(dateAttr);
                if (!parsed.date) throw new Error('Missing date property');
            } catch (e) {
                logger.timeline.store.error(`Invalid timeline item data: "${dateAttr}"`, e);
                timelineElement.innerHTML = '<div class="alert alert-warning m-3">Erreur dans les données de timeline. Certains événements ont des dates invalides.</div>';
                timelineElement.style.opacity = "1";
                this.horizontalTimelineInstance = null;
                this.setStatus('success');
                return;
            }
        }

        // Ensure element has dimensions (may be hidden in inactive tab)
        if (timelineElement.getBoundingClientRect().width === 0) {
            timelineElement.style.width = "100%";
            timelineElement.style.minHeight = "200px";
            void timelineElement.offsetHeight;
        }

        // Prepare leaders data if historical context is enabled
        // Convert ISO dates to fractional years for sub-year positioning
        const isoToFrac = (iso) => {
            const [y, m, d] = iso.split('-').map(Number);
            const s = new Date(y, 0, 1), e = new Date(y + 1, 0, 1), c = new Date(y, m - 1, d);
            return y + (c - s) / (e - s);
        };
        // Convert DD/MM/YYYY dates (KV format) to fractional years
        const ddmmyyyyToFrac = (str) => {
            const [d, m, y] = str.split('/').map(Number);
            const s = new Date(y, 0, 1), e = new Date(y + 1, 0, 1), c = new Date(y, m - 1, d);
            return y + (c - s) / (e - s);
        };
        const showHistorical = timelineFiltersStore.filters.showHistoricalEvents;
        const REGIME_CLASSES = {
            monarchy: 'regime-monarchy', republic: 'regime-republic',
            empire: 'regime-empire', revolution: 'regime-revolution',
            transitional: 'regime-transitional',
        };
        const DYNASTY_CLASSES = {
            merovingiens: 'dynasty-merovingiens', carolingiens: 'dynasty-carolingiens',
            robertiens: 'dynasty-robertiens', capetiens: 'dynasty-capetiens',
            valois: 'dynasty-valois', bourbons: 'dynasty-bourbons',
            orleans: 'dynasty-orleans',
        };
        const leadersData = showHistorical
            ? historicalEventsStore.leaderEvents.map(l => ({
                name: l.title,
                regime: l.regime,
                dynasty: l.dynasty,
                start: l.date,
                end: l.endDate,
                title: l.leaderTitle,
                startFrac: ddmmyyyyToFrac(l.date),
                endFrac:   ddmmyyyyToFrac(l.endDate),
                regimeClass: (l.dynasty ? DYNASTY_CLASSES[l.dynasty] : REGIME_CLASSES[l.regime]) || '',
            }))
            : [];
        const warsData = showHistorical
            ? historicalEventsStore.warEvents.map(w => ({
                name: w.title,
                start: w.date,
                end: w.endDate,
                startFrac: ddmmyyyyToFrac(w.date),
                endFrac: ddmmyyyyToFrac(w.endDate),
            }))
            : [];

        // Instantiate vanilla ES6 timeline using centralized config
        this.horizontalTimelineInstance = new HorizontalTimeline(timelineElement, {
            dateIntervals: TIMELINE_CONFIG.DATE_INTERVALS,
            iconClass: TIMELINE_CONFIG.ICON_CLASS,
            leaders: leadersData,
            wars: warsData,
            yearsGroup: TIMELINE_CONFIG.YEARS_GROUP,
        });

        logger.timeline.store.info(`Timeline initialized with ${timelineItems.length} events`);
    }

    /**
     * Remove the year-pill keyboard handler if present.
     */
    _cleanupYearPillKeyHandler() {
        if (this._yearPillKeyHandler) {
            document.removeEventListener('keydown', this._yearPillKeyHandler);
            this._yearPillKeyHandler = null;
        }
    }

    /**
     * Cleanup the HorizontalTimeline instance and its DOM/event footprint.
     */
    cleanupTimelineInstance() {
        this._cleanupYearPillKeyHandler();
        if (this.horizontalTimelineInstance) {
            this.horizontalTimelineInstance.destroy();
            this.horizontalTimelineInstance = null;
            logger.timeline.store.debug("Timeline instance destroyed");
        }
    }

    clearTimeline() {
        this.cleanupTimelineInstance();

        runInAction(() => {
            this.setStatus('idle');
        });

        const timelineElement = document.getElementById("ascendantTimeline");
        if (timelineElement) {
            timelineElement.innerHTML = '';
        }

        logger.timeline.store.info("🧹 Timeline complètement nettoyée");
    }

    /**
     * Repeuple les événements de timeline pour la root person actuelle.
     * Reproduit la logique de buildHierarchy mais seulement pour les événements.
     */
    populateEventsForCurrentRoot(clearExisting = true) {
        const currentRoot = rootPersonStore.root;
        if (!currentRoot) {
            logger.timeline.store.warn("Aucune root person définie");
            return;
        }

        if (clearExisting) {
            logger.timeline.store.debug("🧹 Vidage des événements existants");
            timelineEventsStore.clearEvents();
        }

        const individualsCache = gedcomDataStore.getIndividualsCache();
        const hierarchy = rootPersonStore.hierarchy;

        if (!hierarchy || !individualsCache) {
            logger.timeline.store.warn("Hiérarchie ou cache individus non disponible");
            return;
        }

        logger.timeline.store.info(`🔄 Traitement des événements pour la hiérarchie (${hierarchy.descendants.length} personnes)`);

        const processIndividual = (individual, sosa = 1) => {
            if (!individual || !individual.i) return;

            const individualData = individualsCache.get(individual.i);
            if (!individualData) return;

            // Phase 10: Utiliser le champ compressé 'e' au lieu de 'individualEvents'
            if (individualData.e && Array.isArray(individualData.e)) {
                const decompressedEvents = getDecompressedEvents(individualData);
                decompressedEvents.forEach(event => {
                    if (['birth', 'death'].includes(event.type)) {
                        const enrichedEvent = { ...event };
                        if (event.townKey || event.town) {
                            const townKey = event.townKey || event.town;
                            const townData = familyTownsStore.getTownsData(townKey);
                            if (townData && townData.town) {
                                enrichedEvent.town = townData.town;
                                if (townData.departement) {
                                    enrichedEvent.town += `, ${townData.departement}`;
                                }
                            }
                        }

                        timelineEventsStore.addEvent({
                            ...enrichedEvent,
                            id: individual.i,
                            personId: individual.i,
                            sosa,
                            name: `${getFirstName(individualData) || ''} ${getSurname(individualData) || ''}`.trim() || 'Individu inconnu'
                        });
                    }
                });
            }

            // Marriage events are in familiesCache, not individual.e
            const familyIds = individualData.fa || [];
            if (familyIds.length > 0) {
                const familiesCache = gedcomDataStore.getFamiliesCache();
                for (const familyId of familyIds) {
                    const family = familiesCache.get(familyId);
                    if (family && family.e && Array.isArray(family.e)) {
                        const familyEvents = family.e
                            .map(decompressEventWithMetadata)
                            .filter(e => e && (e.type === 'marriage' || e.type === 'divorce'));
                        for (const event of familyEvents) {
                            // Dedup: only process marriage from husband side
                            if (family.h && family.w && individual.i !== family.h) continue;

                            // Resolve spouse: m.s fallback to family.h/family.w
                            let spouseName = '';
                            const spouseId = event.m?.s
                                || (individual.i === family.h ? family.w : family.h);
                            if (spouseId) {
                                const spouse = individualsCache.get(spouseId);
                                if (spouse) {
                                    spouseName = `${getFirstName(spouse) || ''} ${getSurname(spouse) || ''}`.trim();
                                }
                            }

                            // Enrich location
                            const enrichedEvent = { ...event };
                            if (event.townKey || event.town) {
                                const townKey = event.townKey || event.town;
                                const townData = familyTownsStore.getTownsData(townKey);
                                if (townData && townData.town) {
                                    enrichedEvent.town = townData.town;
                                    if (townData.departement) {
                                        enrichedEvent.town += `, ${townData.departement}`;
                                    }
                                }
                            }

                            timelineEventsStore.addEvent({
                                ...enrichedEvent,
                                eventId: `${familyId}#${event.type}#${event.date || ''}`,
                                id: individual.i,
                                personId: individual.i,
                                sosa,
                                name: `${getFirstName(individualData) || ''} ${getSurname(individualData) || ''}`.trim() || 'Individu inconnu',
                                spouse: spouseName
                            });
                        }
                    }
                }
            }

            if (individual.children && Array.isArray(individual.children)) {
                individual.children.forEach((child, index) => {
                    processIndividual(child, sosa * 2 + index);
                });
            }
        };

        processIndividual(hierarchy.descendants[0], 1);
        logger.timeline.store.info(`✅ Événements repeuplés: ${timelineEventsStore.events.length} événements ajoutés`);
    }

    dispose() {
        if (this.rootReactionDisposer) {
            this.rootReactionDisposer();
        }
        if (this.filtersReactionDisposer) {
            this.filtersReactionDisposer();
        }
        if (this.tabShownDisposer) {
            this.tabShownDisposer();
        }
        if (this.historicalEventsDisposer) {
            this.historicalEventsDisposer();
        }
        if (this.historicalLoadDisposer) {
            this.historicalLoadDisposer();
        }
        this.clearTimeline();
    }
}

const timelineStore = new TimelineStore();
export default timelineStore;
